import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  api,
  editionsOf,
  errorMessage,
  type AuditEntry,
  type Customer,
  type InvoiceRecord,
  type LicenseDetail,
  type LicenseSubscription,
  type Page,
  type ProductDetail,
} from '../lib/api';
import { useAsync } from '../lib/hooks';
import { fmtDate, fmtDateTime, fmtRelative, shortFp } from '../lib/format';
import { ActionButton, CopyButton, ErrorAlert, Field, LicenseStatusBadge, Loading, Modal, PageHeader, Spinner, modeLabel, typeLabel } from '../components/ui';
import { AuditTable } from './Audit';
import { useT } from '../i18n';

/** Color de cada estado de la suscripción de Stripe. */
const SUBSCRIPTION_TONE: Record<string, string> = {
  active: 'success',
  trialing: 'success',
  past_due: 'warning',
  incomplete: 'warning',
  unpaid: 'danger',
  canceled: 'danger',
  incomplete_expired: 'danger',
  paused: 'danger',
};

/** Suscripción de Stripe de una licencia comprada desde la app: la licencia la sigue sola. */
function SubscriptionCard({ sub }: { sub: LicenseSubscription }) {
  const t = useT();
  const status = sub.status ?? '';
  const label = SUBSCRIPTION_TONE[status] ? t(`licenseDetail.subscriptionStatus.${status as 'active'}`) : status || '—';
  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div className="card-header">
        <h2>{t('licenseDetail.subscription')}</h2>
        {sub.dashboard_url && (
          <a className="btn btn-sm" href={sub.dashboard_url} target="_blank" rel="noreferrer">{t('licenseDetail.openInStripe')}</a>
        )}
      </div>
      <dl className="props">
        <dt>{t('licenseDetail.subscriptionPlan')}</dt>
        <dd>{sub.plan ? <code>{sub.plan}</code> : '—'}</dd>
        <dt>{t('licenseDetail.subscriptionState')}</dt>
        <dd>
          <span className={`badge ${SUBSCRIPTION_TONE[status] ?? ''}`}>{label}</span>
          {sub.cancel_at_period_end && status !== 'canceled' && (
            <> <span className="badge warning plain">{t('licenseDetail.subscriptionCanceling')}</span></>
          )}
        </dd>
        <dt>{sub.cancel_at_period_end ? t('licenseDetail.subscriptionEnds') : t('licenseDetail.subscriptionRenews')}</dt>
        <dd>{sub.current_period_end ? `${fmtDate(sub.current_period_end)} (${fmtRelative(sub.current_period_end)})` : '—'}</dd>
        <dt>{t('licenseDetail.stripeCustomer')}</dt>
        <dd>{sub.stripe_customer_id ? <code>{sub.stripe_customer_id}</code> : '—'}</dd>
      </dl>
      <p className="muted">{t('licenseDetail.subscriptionHelp')}</p>
    </section>
  );
}

const INVOICE_TONE: Record<string, string> = { issued: 'success', draft: 'info', pending: 'warning', failed: 'danger', skipped: '' };

/** Facturas y rectificativas de la suscripción en el programa de facturación (Holded). */
function InvoicesCard({ invoices, onRetry }: { invoices: InvoiceRecord[]; onRetry: (id: string) => void }) {
  const t = useT();
  const importe = (i: InvoiceRecord) =>
    i.amount_total === null
      ? '—'
      : new Intl.NumberFormat(undefined, { style: 'currency', currency: (i.currency ?? 'eur').toUpperCase() }).format(i.amount_total / 100);
  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div className="card-header">
        <h2>{t('licenseDetail.invoices')}</h2>
      </div>
      <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t('licenseDetail.invoiceNumber')}</th>
            <th>{t('licenseDetail.invoiceKind')}</th>
            <th>{t('licenseDetail.invoiceAmount')}</th>
            <th>{t('licenseDetail.subscriptionState')}</th>
            <th>{t('licenseDetail.createdAt')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {invoices.map((i) => (
            <tr key={i.id}>
              <td>{i.external_number ?? <span className="muted">—</span>}</td>
              <td>{t(`licenseDetail.invoiceKinds.${i.kind}`)}</td>
              <td>{importe(i)}</td>
              <td>
                <span className={`badge ${INVOICE_TONE[i.status] ?? ''}`}>{t(`licenseDetail.invoiceStatus.${i.status}`)}</span>
                {i.last_error && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{i.last_error}</div>}
              </td>
              <td>{fmtDateTime(i.created_at)}</td>
              <td>
                {(i.status === 'failed' || i.status === 'draft') && (
                  <button className="btn btn-sm" onClick={() => onRetry(i.id)}>{t('licenseDetail.invoiceRetry')}</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <p className="muted">{t('licenseDetail.invoicesHelp')}</p>
    </section>
  );
}

export function LicenseDetailPage() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const lic = useAsync(() => api.get<LicenseDetail>(`/v1/admin/licenses/${id}`), [id]);
  const audit = useAsync(() => api.get<Page<AuditEntry>>(`/v1/admin/audit?license_id=${id}&page_size=30`), [id]);
  const product = useAsync(() => (lic.data ? api.get<ProductDetail>(`/v1/admin/products/${lic.data.product_id}`) : Promise.resolve(null)), [lic.data?.product_id]);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<null | 'extend' | 'seats' | 'entitlements' | 'customer'>(null);
  if (!lic.data) return lic.error ? <ErrorAlert error={lic.error} /> : <Loading />;
  const l = lic.data;

  const act = (fn: () => Promise<unknown>) => async () => {
    setError(null);
    try {
      await fn();
      await Promise.all([lic.reload(), audit.reload()]);
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const reloadAll = async () => {
    setModal(null);
    await Promise.all([lic.reload(), audit.reload()]);
  };
  const activeActs = l.activations.filter((a) => a.active);
  const pastActs = l.activations.filter((a) => !a.active);
  const reason = () => window.prompt(t('licenseDetail.reasonPrompt')) ?? undefined;

  return (
    <>
      <PageHeader
        crumb={<Link to="/licencias">{t('licenseDetail.back')}</Link>}
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="key">{l.key}</span> <CopyButton text={l.key} /> <LicenseStatusBadge status={l.status} expiresAt={l.expires_at} />
          </span>
        }
        subtitle={[l.product_name, l.edition_name, typeLabel(t, l.type), modeLabel(t, l.mode)].filter(Boolean).join(' · ')}
        actions={
          <>
            {l.status === 'active' && (
              <ActionButton className="btn" confirm={t('licenseDetail.suspendConfirm')} onAction={act(() => api.post(`/v1/admin/licenses/${l.id}/suspend`, { reason: reason() }))}>
                {t('licenseDetail.suspend')}
              </ActionButton>
            )}
            {l.status === 'suspended' && (
              <ActionButton className="btn" onAction={act(() => api.post(`/v1/admin/licenses/${l.id}/reactivate`))}>
                {t('licenseDetail.reactivate')}
              </ActionButton>
            )}
            <button className="btn" onClick={() => setModal('extend')}>{t('licenseDetail.extend')}</button>
            <button className="btn" onClick={() => setModal('seats')}>{t('licenseDetail.changeSeats')}</button>
            {l.status !== 'revoked' && (
              <ActionButton className="btn btn-danger" confirm={t('licenseDetail.revokeConfirm')} onAction={act(() => api.post(`/v1/admin/licenses/${l.id}/revoke`, { reason: reason() }))}>
                {t('licenseDetail.revoke')}
              </ActionButton>
            )}
          </>
        }
      />
      <ErrorAlert error={error} />

      <div className="grid grid-2">
        <section className="card">
          <h2>{t('licenseDetail.data')}</h2>
          <dl className="props">
            <dt>{t('common.customer')}</dt>
            <dd>
              {l.customer_id ? <Link to={`/clientes/${l.customer_id}`}>{l.customer_name}</Link> : '—'}{' '}
              <button className="btn btn-sm" onClick={() => setModal('customer')}>{t('licenseDetail.changeCustomer')}</button>
            </dd>
            <dt>{t('common.product')}</dt>
            <dd><Link to={`/productos/${l.product_id}`}>{l.product_name}</Link> <code className="muted">{l.product_code}</code></dd>
            <dt>{t('licenseDetail.edition')}</dt>
            <dd>{l.edition_id ? <>{l.edition_name} <code className="muted">{l.edition_code}</code></> : <span className="muted">{t('licenseDetail.noEdition')}</span>}</dd>
            <dt>{l.mode === 'floating' ? t('licenseDetail.sessions') : t('licenseDetail.seats')}</dt>
            <dd>{l.mode === 'floating' ? l.seats : t('licenseDetail.inUse', { used: activeActs.length, seats: l.seats })}</dd>
            <dt>{t('licenseDetail.lastSeen')}</dt>
            <dd>
              {l.last_seen_at ? (
                <>
                  {fmtDateTime(l.last_seen_at)} <span className="muted">({fmtRelative(l.last_seen_at)})</span>
                </>
              ) : (
                <span className="muted">{l.last_seen_at === null ? t('lastSeen.never') : '—'}</span>
              )}
              {l.offline_only && <> <span className="badge info plain" title={t('lastSeen.offlineOnlyHelp')}>{t('lastSeen.offline')}</span></>}
            </dd>
            <dt>{t('licenseDetail.expires')}</dt>
            <dd>{l.expires_at ? `${fmtDate(l.expires_at)} (${fmtRelative(l.expires_at)})` : t('common.never')}</dd>
            <dt>{t('licenseDetail.updatesUntil')}</dt>
            <dd>{l.updates_until ? fmtDate(l.updates_until) : l.type === 'perpetual' ? t('licenseDetail.noUpdatesPeriod') : '—'}</dd>
            <dt>{t('licenseDetail.offlineGrace')}</dt>
            <dd>
              {l.offline_grace_days !== null
                ? t('licenseDetail.graceCustom', { n: l.offline_grace_days })
                : t('licenseDetail.graceProduct', { n: product.data?.offline_grace_days ?? '…' })}
            </dd>
            {l.suspended_reason && (
              <>
                <dt>{t('licenseDetail.reason')}</dt>
                <dd>{l.suspended_reason}</dd>
              </>
            )}
            <dt>{t('licenseDetail.createdAt')}</dt>
            <dd>{fmtDateTime(l.created_at)}</dd>
            <dt>{t('common.notes')}</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>{l.notes || '—'}</dd>
          </dl>
        </section>
        <section className="card">
          <div className="card-header">
            <h2>{t('licenseDetail.entitlements')}</h2>
            <button className="btn btn-sm" onClick={() => setModal('entitlements')}>{t('common.edit')}</button>
          </div>
          <dl className="props">
            <dt>{t('licenseDetail.features')}</dt>
            <dd>{l.features.length ? l.features.map((f) => <span key={f} className="tag">{f}</span>) : <span className="muted">{t('common.noneF')}</span>}</dd>
            <dt>{t('licenseDetail.limits')}</dt>
            <dd>
              {Object.keys(l.limits).length
                ? Object.entries(l.limits).map(([k, v]) => <span key={k} className="tag">{k}={v}</span>)
                : <span className="muted">{t('common.none')}</span>}
            </dd>
            <dt>{t('licenseDetail.maxVersion')}</dt>
            <dd>{l.max_version ?? t('licenseDetail.noLimit')}</dd>
          </dl>
        </section>
      </div>

      {l.subscription && <SubscriptionCard sub={l.subscription} />}
      {/* Un servidor anterior a la facturación no envía `invoices`. */}
      {!!l.invoices?.length && (
        <InvoicesCard invoices={l.invoices} onRetry={(id) => act(() => api.post(`/v1/admin/billing/invoices/${id}/retry`))()} />
      )}

      <section className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <h2>{t('licenseDetail.machines', { n: activeActs.length })}</h2>
        </div>
        <ActivationsTable rows={activeActs} onDeactivate={(aid) => act(() => api.post(`/v1/admin/activations/${aid}/deactivate`))()} />
        {pastActs.length > 0 && (
          <details style={{ marginTop: 12 }}>
            <summary className="muted">{t('licenseDetail.deactivatedList', { n: pastActs.length })}</summary>
            <ActivationsTable rows={pastActs} />
          </details>
        )}
      </section>

      {l.mode === 'floating' && (
        <section className="card">
          <h2>{t('licenseDetail.floatingSessions')}</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('licenseDetail.colMachine')}</th>
                  <th>{t('licenseDetail.colSince')}</th>
                  <th>{t('licenseDetail.colLastHeartbeat')}</th>
                  <th>{t('licenseDetail.colExpires')}</th>
                  <th>{t('common.status')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {l.leases.length === 0 && <tr><td colSpan={6} className="empty">{t('licenseDetail.noSessions')}</td></tr>}
                {l.leases.map((s) => (
                  <tr key={s.id}>
                    <td className="mono">{shortFp(s.fingerprint)}</td>
                    <td>{fmtDateTime(s.checked_out_at)}</td>
                    <td>{fmtRelative(s.last_heartbeat_at)}</td>
                    <td>{fmtDateTime(s.expires_at)}</td>
                    <td>
                      {s.active ? (
                        <span className="badge success">{t('licenseDetail.leaseInUse')}</span>
                      ) : (
                        <span className="badge">{s.released_at ? t('licenseDetail.leaseReturned') : t('licenseDetail.leaseExpired')}</span>
                      )}
                    </td>
                    <td className="num">
                      {s.active && (
                        <ActionButton className="btn btn-sm" onAction={act(() => api.post(`/v1/admin/leases/${s.id}/release`))}>
                          {t('licenseDetail.release')}
                        </ActionButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="card">
        <h2>{t('licenseDetail.history')}</h2>
        {audit.data && <AuditTable rows={audit.data.items} compact />}
      </section>

      {modal === 'extend' && <ExtendModal license={l} onClose={() => setModal(null)} onDone={reloadAll} />}
      {modal === 'seats' && <SeatsModal license={l} onClose={() => setModal(null)} onDone={reloadAll} />}
      {modal === 'customer' && <CustomerModal license={l} onClose={() => setModal(null)} onDone={reloadAll} />}
      {modal === 'entitlements' && product.data && (
        <EntitlementsModal license={l} product={product.data} onClose={() => setModal(null)} onDone={reloadAll} />
      )}
    </>
  );
}

function ActivationsTable({ rows, onDeactivate }: { rows: LicenseDetail['activations']; onDeactivate?: (id: string) => Promise<unknown> }) {
  const t = useT();
  const reasonText = (r: string | null) =>
    r === 'client' ? t('licenseDetail.reasonClient') : r === 'admin' ? t('licenseDetail.reasonAdmin') : (r ?? '—');
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t('licenseDetail.colDevice')}</th>
            <th>{t('licenseDetail.colSystem')}</th>
            <th>{t('licenseDetail.colVersion')}</th>
            <th>{t('licenseDetail.colActivated')}</th>
            <th>{t('licenseDetail.colLastSeen')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="empty">{t('licenseDetail.noMachines')}</td></tr>}
          {rows.map((a) => (
            <tr key={a.id}>
              <td>
                <div>{a.machine_name ?? '—'} {a.offline && <span className="badge info plain">{t('licenseDetail.offlineTag')}</span>}</div>
                <div className="mono muted" title={a.fingerprint}>{shortFp(a.fingerprint)}</div>
              </td>
              <td>{a.os ?? '—'}</td>
              <td>{a.app_version ?? '—'}</td>
              <td>{fmtDateTime(a.activated_at)}</td>
              <td title={fmtDateTime(a.last_seen_at)}>
                {a.deactivated_at
                  ? t('licenseDetail.deactivatedAgo', { when: fmtRelative(a.deactivated_at), reason: reasonText(a.deactivated_reason) })
                  : fmtRelative(a.last_seen_at)}
              </td>
              <td className="num">
                {onDeactivate && a.active && (
                  <ActionButton
                    className="btn btn-sm btn-danger"
                    confirm={t('licenseDetail.deactivateConfirm', { name: a.machine_name ?? t('licenseDetail.thisMachine') })}
                    onAction={() => onDeactivate(a.id)}
                  >
                    {t('licenseDetail.deactivate')}
                  </ActionButton>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function useSubmit(fn: () => Promise<unknown>, onDone: () => Promise<void>) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return { error, busy, submit };
}

function FormActions({ busy, onClose, label }: { busy: boolean; onClose: () => void; label: string }) {
  const t = useT();
  return (
    <div className="form-actions">
      <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn btn-primary" disabled={busy}>{busy && <Spinner />} {label}</button>
    </div>
  );
}

function ExtendModal({ license, onClose, onDone }: { license: LicenseDetail; onClose: () => void; onDone: () => Promise<void> }) {
  const t = useT();
  const [days, setDays] = useState(365);
  const [updatesUntil, setUpdatesUntil] = useState(license.updates_until?.slice(0, 10) ?? '');
  const perpetual = license.type === 'perpetual';
  const { error, busy, submit } = useSubmit(
    () => api.post(`/v1/admin/licenses/${license.id}/extend`, perpetual ? { updates_until: new Date(updatesUntil).toISOString() } : { days }),
    onDone,
  );
  return (
    <Modal title={perpetual ? t('licenseDetail.extendUpdatesTitle') : t('licenseDetail.extendTitle')} onClose={onClose}>
      <ErrorAlert error={error} />
      <form onSubmit={submit}>
        {perpetual ? (
          <Field label={t('licenseDetail.updatesUntil')}>
            <input type="date" required value={updatesUntil} onChange={(e) => setUpdatesUntil(e.target.value)} />
          </Field>
        ) : (
          <Field label={t('licenseDetail.daysToAdd')} help={t('licenseDetail.daysToAddHelp', { date: fmtDate(license.expires_at) })}>
            <input type="number" min={1} value={days} onChange={(e) => setDays(Number(e.target.value))} />
          </Field>
        )}
        <FormActions busy={busy} onClose={onClose} label={t('licenseDetail.extend')} />
      </form>
    </Modal>
  );
}

function SeatsModal({ license, onClose, onDone }: { license: LicenseDetail; onClose: () => void; onDone: () => Promise<void> }) {
  const t = useT();
  const [seats, setSeats] = useState(license.seats);
  const { error, busy, submit } = useSubmit(() => api.post(`/v1/admin/licenses/${license.id}/seats`, { seats }), onDone);
  return (
    <Modal title={t('licenseDetail.seatsTitle')} onClose={onClose}>
      <ErrorAlert error={error} />
      <form onSubmit={submit}>
        <Field label={license.mode === 'floating' ? t('licenseDetail.sessions') : t('licenseDetail.seats')} help={t('licenseDetail.seatsHelp')}>
          <input type="number" min={1} value={seats} onChange={(e) => setSeats(Number(e.target.value))} />
        </Field>
        <FormActions busy={busy} onClose={onClose} label={t('common.save')} />
      </form>
    </Modal>
  );
}

function CustomerModal({ license, onClose, onDone }: { license: LicenseDetail; onClose: () => void; onDone: () => Promise<void> }) {
  const t = useT();
  const customers = useAsync(() => api.get<Page<Customer>>('/v1/admin/customers?page_size=500'), []);
  const [customerId, setCustomerId] = useState(license.customer_id ?? '');
  const { error, busy, submit } = useSubmit(
    () => api.patch(`/v1/admin/licenses/${license.id}`, { customer_id: customerId || null }),
    onDone,
  );
  return (
    <Modal title={t('licenseDetail.customerTitle')} onClose={onClose}>
      <ErrorAlert error={error ?? customers.error} />
      <form onSubmit={submit}>
        <Field label={t('common.customer')} help={t('licenseDetail.customerHelp')}>
          <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} disabled={!customers.data}>
            <option value="">{t('createLicense.noCustomer')}</option>
            {customers.data?.items.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </Field>
        {license.subscription && <p className="muted">{t('licenseDetail.customerStripeNote')}</p>}
        <FormActions busy={busy} onClose={onClose} label={t('common.save')} />
      </form>
    </Modal>
  );
}

function EntitlementsModal({ license, product, onClose, onDone }: { license: LicenseDetail; product: ProductDetail; onClose: () => void; onDone: () => Promise<void> }) {
  const t = useT();
  const editions = editionsOf(product);
  const [editionId, setEditionId] = useState(license.edition_id ?? '');
  const [features, setFeatures] = useState<string[]>(license.features);
  const [limits, setLimits] = useState(JSON.stringify(license.limits, null, 2));
  // Elegir una edición carga sus features y límites; se pueden ajustar antes de guardar.
  const chooseEdition = (id: string) => {
    setEditionId(id);
    const e = editions.find((x) => x.id === id);
    if (e) {
      setFeatures([...e.features]);
      setLimits(JSON.stringify(e.limits, null, 2));
    }
  };
  const [maxVersion, setMaxVersion] = useState(license.max_version ?? '');
  const [notes, setNotes] = useState(license.notes ?? '');
  const { error, busy, submit } = useSubmit(async () => {
    let parsed: Record<string, number>;
    try {
      parsed = JSON.parse(limits || '{}') as Record<string, number>;
    } catch {
      throw new Error(t('licenseDetail.limitsInvalid'));
    }
    const editionChange = (editionId || null) !== (license.edition_id ?? null) ? { edition_id: editionId || null } : {};
    await api.patch(`/v1/admin/licenses/${license.id}`, { ...editionChange, features, limits: parsed, max_version: maxVersion || null, notes: notes || null });
  }, onDone);
  return (
    <Modal title={t('licenseDetail.entitlementsTitle')} onClose={onClose}>
      <ErrorAlert error={error} />
      <form onSubmit={submit} className="stack">
        {(editions.length > 0 || license.edition_id) && (
          <Field label={t('licenseDetail.edition')} help={t('licenseDetail.editionHelp')}>
            <select value={editionId} onChange={(e) => chooseEdition(e.target.value)}>
              <option value="">{t('createLicense.noEdition')}</option>
              {editions.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </Field>
        )}
        <Field label={t('licenseDetail.features')}>
          <div className="checks">
            {product.features.map((f) => (
              <label key={f.id}>
                <input type="checkbox" checked={features.includes(f.code)} onChange={(e) => setFeatures(e.target.checked ? [...features, f.code] : features.filter((x) => x !== f.code))} />
                {f.name}
              </label>
            ))}
          </div>
        </Field>
        <Field label={t('licenseDetail.limitsJson')}>
          <textarea className="mono" value={limits} onChange={(e) => setLimits(e.target.value)} />
        </Field>
        <Field label={t('licenseDetail.maxVersion')} help={t('licenseDetail.maxVersionHelp')}>
          <input value={maxVersion} onChange={(e) => setMaxVersion(e.target.value)} />
        </Field>
        <Field label={t('common.notes')}>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <p className="muted" style={{ margin: 0 }}>{t('licenseDetail.changesNote')}</p>
        <FormActions busy={busy} onClose={onClose} label={t('common.save')} />
      </form>
    </Modal>
  );
}
